import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountGroup, AccountGroupService, ProjectionFilter } from '@galaxy/account-group';
import { BillingService, Frequency, ProductPricing } from '@galaxy/billing';
import {
  ItemSelectorConfig,
  ItemSelectorFilterType,
  ItemWhitelabelService,
  LineItem as InventoryLineItem,
  LineItemCacheService,
  SelectedItem,
  SelectionChange,
  SelectionChangeEventType,
  SelectionOptions,
} from '@galaxy/inventory-ui';
import { TranslateService } from '@ngx-translate/core';
import { CommonProperties, ProductAnalyticsService } from '@vendasta/product-analytics';
import { LineItem as SalesLineItem, Order, Revenue, RevenueComponent, RevenuePeriod } from '@vendasta/sales-orders';
import moment from 'moment';
import { combineLatest, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, filter, map, shareReplay, startWith, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { AppConfigService } from '../../app-config.service';
import { UnifiedTOSDialogComponent } from '../../common/tos-unified/unified-tos-dialog.component';
import { FeatureFlags } from '../../core/features';
import { UnifiedTOSService } from '../../core/tos-unified.service';
import { ManageStoreService } from '../../marketplace-app/store/manage-store.service';
import { ActivationNavigationService } from '../core/activation-navigation.service';
import { ActivationService } from '../core/activation.service';
import {
  ActivatableItem,
  ActivatableItemCount,
  ActivatablePackage,
  ActivationStatus,
  ProductBoxItems,
} from '../core/interface';
import { ProductOrderFormStoreService } from '../core/product-order-form.store.service';
import { ActivatableItemCountToSelectedItem, ProductBoxItemsToSelectedItem } from './adapters';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';

const BASE_COMMON_FIELDS = [
  {
    id: 'business_account_group_id',
    name: 'Business ID',
  },
  {
    id: 'business_name',
    name: 'Business Name',
  },
  {
    id: 'business_address',
    name: 'Business Address',
  },
  {
    id: 'business_phone_number',
    name: 'Business Phone Number',
  },
  {
    id: 'business_website',
    name: 'Business Website',
  },
  {
    id: 'contact_name',
    name: 'Contact Name',
  },
  {
    id: 'contact_email',
    name: 'Contact Email',
  },
  {
    id: 'contact_phone_number',
    name: 'Contact Phone Number',
  },
  {
    id: 'salesperson_name',
    name: 'Salesperson Name',
  },
  {
    id: 'salesperson_email',
    name: 'Salesperson Email',
  },
  {
    id: 'salesperson_phone_number',
    name: 'Salesperson Phone Number',
  },
];

@Component({
  selector: 'app-activation-page',
  templateUrl: './activation-selection-page.component.html',
  styleUrls: ['./activation-selection-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivationSelectionPageComponent implements OnInit, OnDestroy {
  activeItems$: Observable<ProductBoxItems[]>;
  selectedAppCartItems$: Observable<ActivatableItemCount[]>;
  appRetailPricingMap$: Observable<{ [sku: string]: ProductPricing }>;
  disableCheckout$: Observable<boolean>;
  hidePricing$: Observable<boolean>;
  marketId$: Observable<string>;
  previousPageUrl$: Observable<string>;
  previousPageName$: Observable<string>;
  checkedOut: boolean;
  accountGroupId: string;
  selectedItemLength = 0;
  subscriptions: Subscription[] = [];
  accountGroup$: Observable<AccountGroup>;

  // product selector
  selectedItems$: Observable<SelectedItem[]>;
  immutableSelectedItems$: Observable<SelectedItem[]>;
  productSelectorConfig$: Observable<ItemSelectorConfig>;
  productSelectorDependenciesLoading$: Observable<boolean>;
  cartConfig$: Observable<SelectionOptions>;

  addItem$$ = new Subject<SelectedItem>();
  removeItem$$ = new Subject<string>();

  // builds an order's line item from product and retail pricing info
  static buildLineItemForOrder(
    item: ActivatableItemCount,
    currencyCode: string,
    appRetailPricingMap: { [p: string]: ProductPricing },
  ): SalesLineItem {
    const retailPricing = appRetailPricingMap ? appRetailPricingMap[item.activatableItem.billingId] : null;
    // if we were unable to fetch the currency code from the retail config, attempt to get it from retail pricing
    if (!currencyCode && retailPricing?.currency) {
      currencyCode = retailPricing.currency;
    }
    const lineItem = ActivationSelectionPageComponent.buildDefaultFreeLineItemForOrder(item, currencyCode);
    if (!retailPricing) {
      // the order will be stamped down as "free" since we were unable to fetch proper retail pricing info
      return lineItem;
    }
    const price = retailPricing.pricingRules[0]?.price || 0;
    let itemFrequency: RevenuePeriod;
    switch (retailPricing.frequency) {
      case Frequency.Yearly:
        itemFrequency = RevenuePeriod.YEARLY;
        break;
      case Frequency.OneTime:
        itemFrequency = RevenuePeriod.ONETIME;
        break;
      default:
        itemFrequency = RevenuePeriod.MONTHLY;
        break;
    }

    const revenueComponents = [
      new RevenueComponent({
        value: price,
        period: itemFrequency,
        isStartingRevenue: retailPricing.isStartingPrice,
      }),
    ];
    if (retailPricing.setupFee > 0) {
      revenueComponents.push(
        new RevenueComponent({
          value: retailPricing.setupFee,
          period: RevenuePeriod.ONETIME,
          isStartingRevenue: false,
        }),
      );
    }

    lineItem.currentRevenue = new Revenue({
      revenueComponents: revenueComponents,
    });

    return lineItem;
  }

  static buildDefaultFreeLineItemForOrder(item: ActivatableItemCount, currencyCode: string): SalesLineItem {
    return new SalesLineItem({
      appKey: {
        appId: item.activatableItem?.appId,
        editionId: item.editionId,
      },
      quantity: item.quantity,
      // Default to free for now, will try to fetch retail pricing after
      currentRevenue: {
        revenueComponents: [{}],
      },
      /**
       * Default to false, as trials are currently not supported in this flow.
       * More investigation needs to be done, as selectedAppCartItems for selected products will have isTrial = true
       * when the account group already has a trial for that product.
       */
      isTrial: false,
      currencyCode: currencyCode,
    });
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private activationService: ActivationService,
    @Inject('PARTNER_ID') readonly partnerId$: Observable<string>,
    private activationNavigationService: ActivationNavigationService,
    private productStoreService: ProductOrderFormStoreService,
    private translate: TranslateService,
    private analytics: ProductAnalyticsService,
    private billingService: BillingService,
    private manageStoreService: ManageStoreService,
    public dialog: MatDialog,
    private tosService: UnifiedTOSService,
    private appConfigService: AppConfigService,
    private accountGroupService: AccountGroupService,
    @Inject(FeatureFlags.PCC_ITEM_SELECTOR_INCLUDES_PACKAGES)
    public readonly pccItemSelectorIncludesPackages$: Observable<boolean>,
    private itemWhitelabelService: ItemWhitelabelService,
    private lineItemCacheService: LineItemCacheService,
    private snackBarService: SnackbarService,
  ) {}

  ngOnInit(): void {
    this.showTOS();
    this.activationService.clearSelections();
    this.marketId$ = this.activationService.accountGroupMarketId$;

    this.cartConfig$ = combineLatest([this.partnerId$, this.marketId$]).pipe(
      map(([partnerId, marketId]) => {
        const config: SelectionOptions = {
          partnerId: partnerId,
          marketId: marketId,
          enforceDependencies: true,
        };
        return config;
      }),
    );

    this.activeItems$ = combineLatest([this.activationService.activeProducts$, this.marketId$]).pipe(
      switchMap(([activeProducts, marketId]) => {
        return this.itemWhitelabelService.whitelabelItems$<ProductBoxItems>(activeProducts, marketId);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this.selectedAppCartItems$ = combineLatest([this.activationService.appCartItems$, this.marketId$]).pipe(
      switchMap(([cartItems, marketId]) => {
        const activatableItems = cartItems.map((appItem) => appItem.activatableItem);
        return this.itemWhitelabelService.whitelabelItems$<ActivatableItem>(activatableItems, marketId);
      }),
      withLatestFrom(this.activationService.appCartItems$),
      map(([whitelabelItems, cartItems]) => {
        return cartItems.map((cartItem) => {
          const whitelabelItem = whitelabelItems.find((item) => item.appId === cartItem.activatableItem.appId);
          return {
            ...cartItem,
            activatableItem: whitelabelItem,
          };
        });
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this.appRetailPricingMap$ = combineLatest([this.selectedAppCartItems$, this.partnerId$, this.marketId$]).pipe(
      switchMap(([appItems, partnerId, marketId]) => {
        const billingIds = appItems.map((appItem) => appItem.activatableItem.billingId);
        return this.billingService.getMultiRetailPricing(partnerId, null, billingIds, marketId);
      }),
      catchError((_) => of(null)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.disableCheckout$ = this.initializeDisableCheckout$();

    this.subscriptions.push(this.activationService.accountGroupId$.subscribe((agid) => (this.accountGroupId = agid)));
    this.subscriptions.push(
      this.selectedAppCartItems$.subscribe((cartItems) => (this.selectedItemLength = cartItems.length)),
    );

    this.previousPageUrl$ = this.route.queryParams.pipe(
      map(({ navigationSource }) =>
        navigationSource === 'orders' ? '/order-management' : `/businesses/accounts/${this.accountGroupId}/details`,
      ),
    );
    this.previousPageName$ = combineLatest([this.activationService.accountGroupName$, this.route.queryParams]).pipe(
      map(([accountGroupName, { navigationSource }]) =>
        navigationSource === 'orders' ? this.translate.instant('SALES_ORDERS.ORDERS') : accountGroupName,
      ),
    );

    this.selectedItems$ = this.pccItemSelectorIncludesPackages$.pipe(
      switchMap((featureEnabled) => {
        const selectedItems$ = this.selectedAppCartItems$.pipe(
          map((items) => {
            return items.map((item) => ActivatableItemCountToSelectedItem(item)) || [];
          }),
        );
        // take(1) is to ensure we can load selected items from the query params initially but not break the
        // new package selector by continuing to emit the selected items
        return featureEnabled ? selectedItems$.pipe(take(1)) : selectedItems$;
      }),
    );
    this.immutableSelectedItems$ = this.activeItems$.pipe(
      map((items) => {
        // remove trials as they are not immutable and can be selected again
        const filteredItems = items?.filter((item) => !item.isTrial) || [];
        return filteredItems?.map((filteredItem) => ProductBoxItemsToSelectedItem(filteredItem)) || [];
      }),
    );

    this.accountGroup$ = this.accountGroupService.get(this.accountGroupId, new ProjectionFilter({ napData: true }));

    this.productSelectorConfig$ = combineLatest([
      this.partnerId$,
      this.marketId$,
      this.accountGroup$,
      this.pccItemSelectorIncludesPackages$,
    ]).pipe(
      map(([partnerId, marketId, accountGroup, packagesFeatureEnabled]) => {
        const itemTypesAvailable: ItemSelectorFilterType[] = ['products'];
        if (packagesFeatureEnabled) {
          itemTypesAvailable.push('packages');
        }
        return {
          partnerId: partnerId,
          marketId: marketId,
          businessId: this.accountGroupId,
          country: accountGroup.napData?.country,
          pageSize: 15,
          priceType: 'instance',
          itemTypesAvailable: itemTypesAvailable,
        };
      }),
    );

    this.productSelectorDependenciesLoading$ = combineLatest([
      this.immutableSelectedItems$,
      this.selectedItems$,
      this.productSelectorConfig$,
    ]).pipe(
      take(1),
      map(() => false),
      startWith(true),
    );

    this.subscriptions.push(
      this.activationService.missingSelectedActivatableItem$.subscribe((missingSelectedItem) => {
        if (missingSelectedItem) {
          this.snackBarService.openErrorSnack(this.translate.instant('SALES_ORDERS.ERRORS.GENERIC_ERROR_REFRESH'));
        }
      }),
    );

    this.setUpAddingRemovingItemStreams();
  }

  /**
   * This is responsible for determining if the terms of service should be shown to the user.
   *
   * The dialog will only be shown if the user:
   * 1. Hasn't accepted the TOS already.
   * 2. The partner is on a value based pricing (VBP) subscription.
   * 3. The user is not impersonating.
   *
   * If the user does not accept the terms of service, they will be sent back to the account details page.
   */
  get shouldShowTOS$(): Observable<boolean> {
    const isImpersonated$ = this.appConfigService.config$.pipe(map((config) => config.isImpersonated));
    return this.tosService.tosDataLoaded$.pipe(
      filter((tosDataReady) => tosDataReady),
      switchMap(() => isImpersonated$),
      take(1),
      map((impersonated) => !this.tosService.hasAcceptedTOS && !this.tosService.isClassicSubscription && !impersonated),
    );
  }

  private showTOS(): void {
    this.shouldShowTOS$
      .pipe(
        switchMap((showTerms) =>
          showTerms
            ? this.dialog.open(UnifiedTOSDialogComponent, UnifiedTOSDialogComponent.config()).afterClosed()
            : of(true),
        ),
      )
      .subscribe((accepted) => {
        if (!accepted) {
          this.router.navigate(['../details'], { relativeTo: this.route });
        }
      });
  }

  removeSelectedApp(appId: string): void {
    const selection = {
      appId: appId,
      quantity: 0,
    };
    this.activationService.selectItem(selection);
  }

  checkoutSelected(): void {
    this.checkedOut = true;

    // we could use the response from appRetailPricingMap$ to get this, but this handles the case when that response is empty
    const currencyConfig$ = combineLatest([this.partnerId$, this.marketId$]).pipe(
      take(1),
      switchMap(([partnerId, marketId]) => {
        return this.manageStoreService.getRetailConfiguration(partnerId, marketId);
      }),
      catchError((_) => of(null)),
    );

    combineLatest([this.selectedAppCartItems$, this.appRetailPricingMap$, currencyConfig$])
      .pipe(
        switchMap(([appCartItems, appRetailPricingMap, currencyConfig]) => {
          this.analytics.trackEvent(
            'activate-products-checkout-selected',
            CommonProperties.ECOMMERCE,
            'has-matching-item',
            0,
            {
              hasAppWithOrderForm: !!appCartItems?.find((item) => item.activatableItem.usesOrderForm),
            },
          );

          //filter to prevent checkout an already activated app when its addon is being activated
          const lineItems = appCartItems
            .filter((item) => {
              return (
                item.activatableItem.allowMultipleActivations ||
                (item.status !== ActivationStatus.ACTIVATED && item.status !== ActivationStatus.PENDING) ||
                item.isTrial ||
                item.activatableItem.editionData?.usesEditions
              );
            })
            .map((item) => {
              const currencyCode = currencyConfig?.currency || '';
              return ActivationSelectionPageComponent.buildLineItemForOrder(item, currencyCode, appRetailPricingMap);
            });
          if (!lineItemsMatchInventory(lineItems, this.lineItemCacheService.readLineItems(this.accountGroupId))) {
            throw Error();
          }

          return combineLatest([
            of(lineItems),
            this.marketId$,
            this.activationService.accountGroupId$,
            this.partnerId$,
          ]);
        }),
        map(([lineItems, marketId, businessId, partnerId]) => {
          const newCommonFields = BASE_COMMON_FIELDS.map((fieldItem) => ({
            field: {
              answer: 'null',
              description: '',
              field_id: fieldItem.id,
              field_type: 1,
              label: fieldItem.name,
              prefix: '',
              regex_error_message: '',
              regex_validator: '',
              suffix: '',
            },
          }));
          return new Order({
            businessId,
            marketId,
            partnerId,
            requestedActivation: moment().toDate(),
            lineItems: lineItems,
            commonFields: newCommonFields,
            tags: [this.translate.instant('SALES_ORDERS.MANUAL_ACTIVATION_TAG')],
          });
        }),
        take(1),
        tap((order) => {
          this.productStoreService.cacheOrder(order);
          this.activationNavigationService.navigateToNextStep('selection', order);
        }),
      )
      .subscribe({
        error: (_) => {
          this.checkedOut = false;
          this.snackBarService.openErrorSnack(this.translate.instant('SALES_ORDERS.ERRORS.GENERIC_ERROR_REFRESH'));
        },
      });
  }

  initializeDisableCheckout$(): Observable<boolean> {
    return this.selectedAppCartItems$.pipe(
      startWith([]),
      map((cartItems) => !cartItems.some((item) => item.quantity > 0)),
    );
  }

  selectedItemsChanged(selectionChange: SelectionChange): void {
    const updatedItem = selectionChange.updatedItem;
    if (selectionChange.eventType === SelectionChangeEventType.DESELECTED) {
      updatedItem.quantity = 0;
      this.removeItem$$.next(updatedItem.itemId);
    } else {
      this.addItem$$.next(updatedItem);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  //TODO: remove this function once packages in the new item selector is officially released
  tabChanged($event: MatTabChangeEvent): void {
    if ($event.index === 0 && $event.tab.textLabel === 'Products') {
      this.analytics.trackEvent('clicked-products-tab', '', 'click');
    } else if ($event.index === 1 && $event.tab.textLabel === 'Packages') {
      this.analytics.trackEvent('clicked-packages-tab', '', 'click');
    }
  }

  private setUpAddingRemovingItemStreams(): void {
    const addSub = combineLatest([this.activationService.activatablePackages$, this.addItem$$])
      .pipe(map(([packages, selectedItem]) => this.addItem(packages, selectedItem)))
      .subscribe();

    const removeSub = combineLatest([this.activationService.activatablePackages$, this.removeItem$$])
      .pipe(map(([packages, item]) => this.removeItem(packages, item)))
      .subscribe();

    this.subscriptions.push(addSub, removeSub);
  }

  private addItem(packages: ActivatablePackage[], selectedItem: SelectedItem): void {
    if (selectedItem.type === 'package') {
      const activatablePackage = packages.find((pkg) => pkg.packageId === selectedItem.itemId);
      if (activatablePackage) {
        const selectedItems = activatablePackage.activatableItemCount.map((item) => ({
          appId: item.activatableItem.appId,
          editionId: item.editionId || '',
          quantity: item.quantity,
        }));
        this.activationService.selectItems(selectedItems);
      }
    } else {
      const editionId = selectedItem.type === 'app' ? selectedItem.edition?.editionId : undefined;
      this.activationService.selectItem({
        appId: selectedItem.itemId,
        editionId: editionId,
        quantity: selectedItem.quantity,
      });
    }
  }

  private removeItem(packages: ActivatablePackage[], selectedItemId: string): void {
    if (isPackageItemId(selectedItemId)) {
      const activatablePackage = packages.find((pkg) => pkg.packageId === selectedItemId);
      if (activatablePackage) {
        const selectedItems = activatablePackage.activatableItemCount.map((item) => ({
          appId: item.activatableItem.appId,
          quantity: 0,
        }));
        this.activationService.selectItems(selectedItems);
      }
    } else {
      this.activationService.selectItem({ appId: selectedItemId, quantity: 0 });
    }
  }
}

function isPackageItemId(itemId: string): boolean {
  return itemId.startsWith('SOL-');
}

function lineItemsMatchInventory(lineItems: SalesLineItem[], cachedLineItems: InventoryLineItem[]): boolean {
  return cachedLineItems
    .filter((item) => item.itemType !== 'package')
    .every((item) => lineItems.find((lineItem) => lineItem.appKey.appId === item.itemId));
}
